import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from 'react'
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Radio,
  RadioGroup,
} from 'react-aria-components'
import { Input } from '@/components/base/input/input'
import { Hinweis } from '@/teile/Hinweis'
import { Knopf } from '@/teile/Knopf'
import { Status } from '@/teile/Status'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import {
  ladeSipgateGeraete,
  ladeSipgateZugang,
  loescheSipgateZugang,
  type SipgateGeraet,
  type SipgateZugang,
  speichereSipgateZugang,
  waehleSipgateGeraet,
} from '../api/einstellungen.js'
import { Seitenkopf } from '../layout/AppShell.js'
import { useSitzung } from '../session/SitzungContext.js'
import { ZweiterFaktor } from '../teile/ZweiterFaktor.js'

const SIPGATE_TOKEN_URL = 'https://app.sipgate.com/personal-access-token'

/**
 * Die nötigen Berechtigungen, und zwar von Anfang an.
 *
 * `devices:read` wird schon beim Hinterlegen gebraucht — ohne sie lässt sich
 * die Geräteliste nicht abrufen und der Token nicht prüfen.
 *
 * Beim Wählen hat sipgate **zwei** Wege mit zwei verschiedenen Rechten:
 * `rtcm:write` für Neo-Anlagen (das aktuelle Produkt) und
 * `sessions:calls:write` für die älteren Classic-Anlagen. Welcher Weg trägt,
 * hängt am Konto und ist von aussen nicht zuverlässig zu erkennen.
 *
 * `rtcm:read` kommt für die Anzeige des laufenden Gesprächs dazu.
 *
 * Alle anzuhaken kostet nichts und erspart eine zweite Runde — Rechte lassen
 * sich einem Token nachträglich nicht hinzufügen, er müsste neu erzeugt werden.
 */
const BENOETIGTE_SCOPES = ['devices:read', 'rtcm:read', 'rtcm:write', 'sessions:calls:write']

function geraeteArt(art: string): string {
  switch (art.toUpperCase()) {
    case 'REGISTER':
      return 'Tischtelefon'
    case 'MOBILE':
      return 'Mobiltelefon'
    case 'APP':
      return 'App'
    default:
      return art
  }
}

export function Einstellungen() {
  const { zustand: sitzung, neuLaden } = useSitzung()
  const [zugang, setZugang] = useState<SipgateZugang | null>(null)
  const [ladefehler, setLadefehler] = useState<string | null>(null)
  const [tokenId, setTokenId] = useState('')
  const [token, setToken] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<{ art: 'erfolg' | 'fehler'; text: string } | null>(null)
  // Das Formular zum Ersetzen ist eingeklappt, solange ein Zugang da ist —
  // wer es braucht, klappt es auf. Fehlt der Zugang, steht es offen: Dann ist
  // es der einzige Grund, auf dieser Seite zu sein.
  const [ersetzenOffen, setErsetzenOffen] = useState(false)

  // Geräteauswahl. `null` heißt: nicht abgerufen. Sie wird nur geholt, wenn
  // wirklich eine Auswahl ansteht — nicht bei jedem Aufruf der Seite, denn das
  // wäre je Seitenaufruf eine Runde zu sipgate.
  const [geraete, setGeraete] = useState<SipgateGeraet[] | null>(null)
  const [geraeteFehler, setGeraeteFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const geladen = await ladeSipgateZugang()
      setZugang(geladen)
      setErsetzenOffen(!geladen.hinterlegt)
      setLadefehler(null)
    } catch (error) {
      setLadefehler(error instanceof ApiError ? error.message : 'Unbekannter Fehler')
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  const geraeteHolen = useCallback(async () => {
    setGeraeteFehler(null)
    try {
      setGeraete((await ladeSipgateGeraete()).geraete)
    } catch (error) {
      setGeraeteFehler(error instanceof ApiError ? error.message : 'Geräte nicht abrufbar.')
    }
  }, [])

  // Ist ein Zugang hinterlegt, aber kein Gerät gesetzt, dann gab es mehr als
  // eines und die Auswahl steht noch aus. Bei genau einem Gerät hat der Dienst
  // es bereits gesetzt — dann passiert hier nichts.
  useEffect(() => {
    if (zugang?.hinterlegt && !zugang.ausgewaehltesGeraet && geraete === null) {
      void geraeteHolen()
    }
  }, [zugang, geraete, geraeteHolen])

  async function speichern(event: FormEvent) {
    event.preventDefault()
    setLaeuft(true)
    setMeldung(null)
    setGeraete(null)

    try {
      const ergebnis = await speichereSipgateZugang({
        tokenId: tokenId.trim(),
        token: token.trim(),
      })
      // Der Klartext verschwindet sofort aus dem Formular — er wird nicht
      // gebraucht und hat im Speicher des Browsers nichts verloren.
      setTokenId('')
      setToken('')
      setZugang(ergebnis.zugang)
      setErsetzenOffen(false)

      if (ergebnis.auswahlNoetig) {
        setGeraete(ergebnis.geraete)
        setMeldung({
          art: 'erfolg',
          text: 'Token geprüft und gespeichert. Bitte wählen Sie noch Ihr Telefon aus.',
        })
      } else {
        setMeldung({
          art: 'erfolg',
          text: `Token geprüft und gespeichert. Telefon: ${
            ergebnis.zugang.ausgewaehltesGeraetName ?? ergebnis.zugang.ausgewaehltesGeraet
          }.`,
        })
      }
    } catch (error) {
      setMeldung({
        art: 'fehler',
        text: error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.',
      })
    } finally {
      setLaeuft(false)
    }
  }

  async function geraetWaehlen(deviceId: string) {
    setLaeuft(true)
    setMeldung(null)
    try {
      const neu = await waehleSipgateGeraet(deviceId)
      setZugang(neu)
      setMeldung({
        art: 'erfolg',
        text: `Telefon gesetzt: ${neu.ausgewaehltesGeraetName ?? neu.ausgewaehltesGeraet}.`,
      })
    } catch (error) {
      setMeldung({
        art: 'fehler',
        text: error instanceof ApiError ? error.message : 'Auswahl fehlgeschlagen.',
      })
    } finally {
      setLaeuft(false)
    }
  }

  async function entfernen() {
    setLaeuft(true)
    setMeldung(null)
    try {
      await loescheSipgateZugang()
      setGeraete(null)
      await laden()
      setMeldung({ art: 'erfolg', text: 'Zugangsdaten entfernt.' })
    } catch (error) {
      setMeldung({
        art: 'fehler',
        text: error instanceof ApiError ? error.message : 'Entfernen fehlgeschlagen.',
      })
    } finally {
      setLaeuft(false)
    }
  }

  // Die Auswahl wird nur gezeigt, wenn es tatsächlich etwas zu entscheiden
  // gibt: mehr als ein Gerät. Eine Liste mit einem einzigen Eintrag ist keine
  // Wahl, sondern eine Rückfrage ohne Inhalt.
  const auswahlZeigen = geraete !== null && geraete.length > 1

  return (
    <div>
      <Seitenkopf
        beisatz="Ihr sipgate-Zugang"
        titel="Einstellungen"
        satz="Ihre Zugangsdaten zu sipgate. Sie bleiben im Dienst Ihres Partners und verlassen ihn nicht."
      />

      <div className="flex flex-col gap-6 px-6 pb-8">
        {ladefehler ? (
          <Hinweis art="fehler" titel="Einstellungen nicht abrufbar">
            {ladefehler}
          </Hinweis>
        ) : null}

        {sitzung.status === 'angemeldet' ? (
          <ZweiterFaktor
            eingerichtet={sitzung.sitzung.zweiterFaktor.eingerichtet}
            pflicht={sitzung.sitzung.zweiterFaktor.pflicht}
            vomPartner={sitzung.sitzung.zweiterFaktor.vomPartner}
            onGeaendert={() => void neuLaden()}
          />
        ) : null}

        <section className="flex flex-col gap-3">
          <div className="flex items-baseline gap-2">
            <h2 className="font-display text-base font-bold text-primary">Hinterlegter Zugang</h2>
            {zugang?.hinterlegt ? (
              <Status ton="gut">hinterlegt</Status>
            ) : (
              <Status ton="wartet">fehlt noch</Status>
            )}
          </div>

          {zugang?.hinterlegt ? (
            <>
              <dl className="flex flex-wrap gap-[30px]">
                <Wert name="Token-ID">
                  <code className="font-mono text-[13px]">{zugang.tokenId}</code>
                </Wert>
                <Wert name="Token">
                  {/* Der Token wird nach dem Speichern nie wieder ausgeliefert —
                      sichtbar sind nur die letzten Zeichen. */}
                  <code className="font-mono text-[13px]">••••••••{zugang.tokenLast4}</code>
                </Wert>
                <Wert name="Zuletzt geprüft">
                  {zugang.zuletztGeprueft
                    ? new Date(zugang.zuletztGeprueft).toLocaleString('de-DE')
                    : 'noch nicht'}
                </Wert>
                <Wert name="Telefon" achtung={!zugang.ausgewaehltesGeraet}>
                  {zugang.ausgewaehltesGeraet
                    ? (zugang.ausgewaehltesGeraetName ?? zugang.ausgewaehltesGeraet)
                    : 'noch nicht gewählt'}
                </Wert>
              </dl>
              <div className="flex flex-wrap gap-2">
                {zugang.ausgewaehltesGeraet ? (
                  <Knopf onClick={() => void geraeteHolen()} disabled={laeuft || auswahlZeigen}>
                    Anderes Telefon wählen
                  </Knopf>
                ) : null}
                <Knopf gefahr onClick={() => void entfernen()} disabled={laeuft}>
                  Zugangsdaten entfernen
                </Knopf>
              </div>
            </>
          ) : (
            <p className="text-[13px] text-tertiary">
              Ohne Zugangsdaten kann die App nicht wählen. Hinterlegen Sie unten Ihren Token.
            </p>
          )}
        </section>

        {geraeteFehler ? (
          <Hinweis art="fehler" titel="Geräte nicht abrufbar">
            {geraeteFehler}
          </Hinweis>
        ) : null}

        {auswahlZeigen ? (
          <section className="flex flex-col gap-3 border-t border-secondary pt-5">
            <div>
              <h2 className="font-display text-base font-bold text-primary">Telefon auswählen</h2>
              <p className="mt-1 text-[13px] text-tertiary">
                In Ihrem sipgate-Konto stehen mehrere Geräte. Auf welchem soll es klingeln, wenn Sie
                den Hotkey drücken?
              </p>
            </div>
            <RadioGroup
              className="flex max-w-[34rem] flex-col gap-2"
              value={zugang?.ausgewaehltesGeraet ?? ''}
              onChange={(id) => void geraetWaehlen(id)}
              isDisabled={laeuft}
              aria-label="Telefon"
            >
              {geraete?.map((geraet) => (
                <Radio
                  key={geraet.id}
                  value={geraet.id}
                  className={({ isSelected }) =>
                    cx(
                      'flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-sm transition-colors',
                      isSelected
                        ? 'border-akzent bg-akzent-flaeche text-primary'
                        : 'border-secondary text-secondary hover:border-rand-aktiv',
                    )
                  }
                >
                  <span className="font-medium">{geraet.name}</span>
                  <span className="flex items-center gap-2 text-[12px] text-tertiary">
                    {geraeteArt(geraet.art)} · {geraet.id}
                    {geraet.online ? (
                      <Status ton="gut">erreichbar</Status>
                    ) : (
                      <Status ton="ruhig">nicht angemeldet</Status>
                    )}
                  </span>
                </Radio>
              ))}
            </RadioGroup>
          </section>
        ) : null}

        <Disclosure
          isExpanded={ersetzenOffen}
          onExpandedChange={setErsetzenOffen}
          className="flex flex-col gap-4 border-t border-secondary pt-5"
        >
          <div>
            <h2 className="font-display text-base font-bold text-primary">
              <AriaButton
                slot="trigger"
                className="flex cursor-pointer items-center gap-2 rounded-md outline-brand focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                {zugang?.hinterlegt ? 'Zugangsdaten ersetzen' : 'Zugangsdaten hinterlegen'}
                {/* Ein Winkel, der sich dreht — derselbe Strich wie beim
                    „Zurueck" der Kundenverwaltung. */}
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                  className={cx(
                    'size-3.5 text-tertiary transition-transform',
                    ersetzenOffen && 'rotate-90',
                  )}
                >
                  <path
                    d="M6 3l5 5-5 5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </AriaButton>
            </h2>
            <p className="mt-1 text-[13px] text-tertiary">
              Sie erzeugen den Token selbst in Ihrem sipgate-Konto. Niemand sonst bekommt Ihre
              Zugangsdaten zu sehen.
            </p>
          </div>

          <DisclosurePanel className="flex flex-col gap-4">
            <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-secondary">
              <li>
                Öffnen Sie die{' '}
                <a
                  className="font-medium text-akzent-tief underline"
                  href={SIPGATE_TOKEN_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Token-Verwaltung bei sipgate
                </a>
                .
              </li>
              <li>
                Erzeugen Sie einen Token mit diesen Berechtigungen:{' '}
                {BENOETIGTE_SCOPES.map((scope, i) => (
                  <span key={scope}>
                    {i > 0 ? ', ' : ''}
                    <code className="rounded-md bg-secondary px-1.5 py-0.5 font-mono text-[12px]">
                      {scope}
                    </code>
                  </span>
                ))}
                . Alle vier anhaken — Rechte lassen sich einem Token später nicht hinzufügen.
              </li>
              <li>Tragen Sie Token-ID und Token hier ein.</li>
            </ol>

            <form onSubmit={speichern} className="flex max-w-[26rem] flex-col gap-3.5">
              <Input
                isRequired
                label="Token-ID"
                size="sm"
                value={tokenId}
                onChange={setTokenId}
                placeholder="token-ABC123"
                autoComplete="off"
              />
              <Input
                isRequired
                type="password"
                label="Token"
                size="sm"
                value={token}
                onChange={setToken}
                placeholder="••••••••"
                autoComplete="off"
              />

              {meldung ? <Hinweis art={meldung.art}>{meldung.text}</Hinweis> : null}

              <div>
                <Knopf type="submit" stark disabled={laeuft || !tokenId.trim() || !token.trim()}>
                  {laeuft ? 'Wird geprüft …' : 'Prüfen und speichern'}
                </Knopf>
              </div>
            </form>
          </DisclosurePanel>
        </Disclosure>
      </div>
    </div>
  )
}

/** Beschriftung ueber Wert — dieselbe Zeile wie auf der Kundenseite der Kundenverwaltung. */
function Wert({
  name,
  children,
  achtung,
}: {
  name: string
  children: ReactNode
  achtung?: boolean
}) {
  return (
    <div>
      <dt className="text-[11.5px] text-quaternary">{name}</dt>
      <dd
        className={cx('mt-0.5 text-sm font-medium', achtung ? 'text-achtung-tief' : 'text-primary')}
      >
        {children}
      </dd>
    </div>
  )
}
